// Rest positions of bristle groups in the brush's own frame.
// u runs across the brush (width axis), v along the direction of travel.
// Both are in units of the brush radius. Each group also gets a rest radius
// (how far from the ferrule centre it sits, used for pressure-dependent
// contact) and per-group variation.

import { createRng } from '../core/rng.js';

const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

export function createBristleLayout(preset, seed = 1) {
  const rng = createRng(seed * 7919 + preset.bristles);
  const count = Math.max(1, preset.bristles | 0);
  const aspect = Math.max(1, preset.aspect || 1);
  const groups = [];
  // Clump centres for split tips: bristles are pulled toward a few clumps.
  const clumpCount = Math.max(1, Math.round(2 + preset.separation * 6));
  const clumps = Array.from({ length: clumpCount }, () => [rng.range(-1, 1), rng.range(-1, 1) / aspect]);

  for (let b = 0; b < count; b += 1) {
    let u = 0;
    let v = 0;
    switch (preset.layout) {
      case 'flat': {
        u = -1 + (2 * (b + 0.5)) / count + rng.range(-0.5, 0.5) / count;
        v = rng.range(-1, 1) / aspect;
        break;
      }
      case 'filbert': {
        u = -1 + (2 * (b + 0.5)) / count + rng.range(-0.5, 0.5) / count;
        // Rounded corners: bristles at the ends sit further back (shorter reach).
        v = rng.range(-1, 1) / aspect * Math.sqrt(Math.max(0.05, 1 - u * u));
        break;
      }
      case 'fan': {
        const angle = (-0.5 + (b + 0.5) / count) * 1.6 + rng.range(-0.03, 0.03);
        u = Math.sin(angle) * 1.0;
        v = (Math.cos(angle) - 0.85) * 0.6;
        break;
      }
      case 'sponge': {
        const r = Math.sqrt(rng.next());
        const a = rng.next() * Math.PI * 2;
        u = r * Math.cos(a);
        v = (r * Math.sin(a)) / aspect;
        break;
      }
      case 'round':
      default: {
        // Sunflower packing gives an even disc of bristle groups.
        const r = Math.sqrt((b + 0.5) / count);
        const a = b * GOLDEN_ANGLE;
        u = r * Math.cos(a);
        v = r * Math.sin(a);
      }
    }
    if (preset.separation > 0 && preset.layout !== 'sponge') {
      let nearest = clumps[0];
      let best = Infinity;
      for (const c of clumps) {
        const d = (c[0] - u) ** 2 + (c[1] - v) ** 2;
        if (d < best) {
          best = d;
          nearest = c;
        }
      }
      const pull = preset.separation * 0.35;
      u += (nearest[0] - u) * pull;
      v += (nearest[1] - v) * pull;
    }
    const jitter = preset.jitter || 0;
    groups.push({
      u,
      v,
      rest: Math.min(1, Math.hypot(u, v * aspect)),
      lengthScale: 1 + jitter * rng.range(-0.5, 0.5),
      stiffness: Math.max(0.02, Math.min(1, preset.stiffness * (1 + jitter * rng.range(-0.4, 0.4)))),
      capacityScale: 1 + jitter * rng.range(-0.4, 0.4),
      poreRadius: preset.layout === 'sponge' ? rng.range(0.05, 0.16) : 0,
    });
  }
  return groups;
}
